package com.essntl.features.contact.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.essntl.features.address.domain.model.AddressModel
import com.essntl.features.address.ui.AddressEvent
import com.essntl.features.address.ui.AddressScreen
import com.essntl.features.address.ui.AddressState
import com.essntl.features.address.ui.AddressValidation
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.input.EsPhoneInput
import com.essntl.uikit.components.input.date.EsDateInput
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography

@Composable
fun ContactScreen(
    state: ContactState,
    onEvent: (ContactEvent) -> Unit,
    validation: ContactValidation,
    addressState: AddressState,
    onAddressEvent: (AddressEvent) -> Unit,
    addressValidation: AddressValidation,
    dialog: Boolean = false,
) {
    LaunchedEffect(state.getContact.data) {
        validation.contactForm.reset()
        validation.contactForm.name.value = state.getContact.data?.prettyName.orEmpty()
    }

    LaunchedEffect(state.addAddress) {
        if (!state.addAddress) {
            onAddressEvent(AddressEvent.UpdateAddressFields(AddressModel.empty))
            addressValidation.addressForm.reset()
        }
    }

    val isEdit by remember(state.contact.value.id) {
        mutableStateOf(
            state.contact.value.id.isNotBlank(),
        )
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(
                color = MaterialTheme.esColorScheme.bgSecondary,
                shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
            )
            .clip(
                RoundedCornerShape(
                    topStart = 24.dp,
                    topEnd = 24.dp,
                ),
            )
            .padding(
                horizontal = 2.5f.spacing,
                vertical = 2.spacing,
            ),
    ) {
        Column(
            Modifier.then(
                if (dialog) Modifier
                    .verticalScroll(rememberScrollState())
                    .weight(1f)
                else Modifier.fillMaxSize(),
            ),
        ) {
            Text(
                text = "Contact information",
                textAlign = TextAlign.Start,
                style = MaterialTheme.esTypography.p1Bold16,
            )

            Spacer(modifier = Modifier.height(1.spacing))

            Text(
                text = "Add a new contact to the agency above by completing the fields below.",
                color = MaterialTheme.esColorScheme.textSecondary,
                style = MaterialTheme.esTypography.p1Medium16,
            )

            Spacer(modifier = Modifier.height(1.spacing))

            if (state.getContact.isLoading)
                Box(
                    contentAlignment = Alignment.Center,
                    modifier =
                        Modifier
                            .fillMaxWidth()
                            .height(200.dp)
                            .padding(2.5f.spacing),
                ) {
                    EsSpinner()
                }
            else {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(3.spacing),
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    EsInput(
                        value = state.contact.value.firstName.orEmpty(),
                        onValueChange = {
                            validation.contactForm.name.validate(it + state.contact.value.lastName.orEmpty())

                            onEvent(
                                ContactEvent.UpdateContactFields(
                                    state.contact.value.copy(firstName = it),
                                ),
                            )
                        },
                        label = "First name",
                        message = "First Name or Last Name is required",
                        state = validation.contactForm.name.state,
                        placeholder = "Enter first name",
                        modifier = Modifier.weight(1f),
                    )

                    EsInput(
                        value = state.contact.value.lastName.orEmpty(),
                        onValueChange = {
                            validation.contactForm.name.validate(it + state.contact.value.firstName.orEmpty())

                            onEvent(
                                ContactEvent.UpdateContactFields(
                                    state.contact.value.copy(lastName = it),
                                ),
                            )
                        },
                        label = "Last name",
                        placeholder = "Enter last name",
                        message = "First Name or Last Name is required",
                        state = validation.contactForm.name.state,
                        modifier = Modifier.weight(1f),
                    )
                }

                if (!dialog) {
                    Spacer(modifier = Modifier.height(1.spacing))

                    Row(
                        horizontalArrangement = Arrangement.spacedBy(3.spacing),
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        EsInput(
                            value = state.contact.value.email.orEmpty(),
                            onValueChange = {
                                onEvent(
                                    ContactEvent.UpdateContactFields(
                                        state.contact.value.copy(email = it),
                                    ),
                                )
                            },
                            label = "Email",
                            placeholder = "Enter email",
                            modifier = Modifier.weight(1f),
                        )

                        EsInput(
                            value = state.contact.value.salutation.orEmpty(),
                            onValueChange = {
                                onEvent(
                                    ContactEvent.UpdateContactFields(
                                        state.contact.value.copy(salutation = it),
                                    ),
                                )
                            },
                            label = "Salutation",
                            placeholder = "Enter Salutation",
                            modifier = Modifier.weight(1f),
                        )
                    }
                }

                Spacer(modifier = Modifier.height(1.spacing))

                Row(
                    horizontalArrangement = Arrangement.spacedBy(3.spacing),
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    EsPhoneInput(
                        value = state.contact.value.phoneNumber?.toLongOrNull() ?: 0,
                        onValueChange = { value ->
                            onEvent(
                                ContactEvent.UpdateContactFields(
                                    state.contact.value.copy(phoneNumber = value.toString()),
                                ),
                            )
                        },
                        label = "Telephone",
                        placeholder = "Enter Telephone number",
                        modifier = Modifier.weight(1f),
                    )

                    if (!dialog)
                        EsDateInput(
                            value = state.contact.value.dateOfBirth.orEmpty(),
                            onValueChange = {
                                println("Date of birth: $it")
                                onEvent(
                                    ContactEvent.UpdateContactFields(
                                        state.contact.value.copy(
                                            dateOfBirth = (it),
                                        ),
                                    ),
                                )
                            },
                            label = "Date of birth",
                            modifier = Modifier.weight(1f),
                        )
                }

                if (!dialog) {
                    Spacer(modifier = Modifier.height(1.spacing))

                    EsInput(
                        value = state.contact.value.notes.orEmpty(),
                        onValueChange = {
                            onEvent(
                                ContactEvent.UpdateContactFields(
                                    state.contact.value.copy(notes = it),
                                ),
                            )
                        },
                        label = "Notes",
                        placeholder = "Some Notes",
                        modifier = Modifier.fillMaxWidth(),
                    )

                    Spacer(modifier = Modifier.height(2.5.spacing))

                    if (state.getContact.isLoading ||
                        addressState.getAddress.isLoading
                    ) EsSpinner()
                    else {
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(1.5.spacing),
                            modifier = Modifier
                                .clip(RoundedCornerShape(2.spacing))
                                .clickable(
                                    enabled = addressState.address.value.id.isEmpty(),
                                ) {
                                    onEvent(ContactEvent.AddRemoveAddress())
                                }
                                .padding(1.spacing),
                        ) {
                            Text(
                                text = if (state.addAddress)
                                    if (addressState.address.value.id.isNotEmpty())
                                        "Edit address" else
                                        "Remove address" else
                                    "Add address",
                                textAlign = TextAlign.Start,
                                style = MaterialTheme.esTypography.p1Medium16,
                                color = if (state.addAddress)
                                    if (addressState.address.value.id.isNotEmpty())
                                        MaterialTheme.esColorScheme.textAccent else
                                        MaterialTheme.esColorScheme.textError else
                                    MaterialTheme.esColorScheme.textAccent,
                            )

                            if (addressState.address.value.id.isEmpty())
                                EsLargeIcon(
                                    res = if (state.addAddress)
                                        EsIcons.ArrowDown else
                                        EsIcons.Add,
                                    tint = if (state.addAddress)
                                        MaterialTheme.esColorScheme.iconError else
                                        MaterialTheme.esColorScheme.iconAccent,
                                )
                        }

                        if (state.addAddress)
                            AddressScreen(
                                state = addressState,
                                onEvent = onAddressEvent,
                                validation = addressValidation,
                                innerScreen = true,
                            )
                    }
                }

                if (dialog) {
                    Spacer(modifier = Modifier.height(1.dp))

                    Box(
                        modifier =
                            Modifier
                                .background(MaterialTheme.esColorScheme.bgSecondary)
                                .padding(2.5.spacing),
                    ) {
                        EsLargePrimaryButton(
                            onClick = {
                                if (validation.contactForm.validate()) {
                                    if (isEdit)
                                        onEvent(ContactEvent.EditContact) else
                                        onEvent(ContactEvent.AddContact)
                                }
                            },
                            text = "Save",
                            loading = state.editContact.isLoading,
                            modifier = Modifier.fillMaxWidth(),
                        )
                    }
                }
            }
        }
    }
}